/**
 * Cliente HTTP.
 *
 * O sistema atual usa `fetch()` solto em cada tela, sem tratamento comum de
 * erro, sem timeout e sem distinguir "banco fora do ar" de "OS não existe" —
 * tudo vira o mesmo modal "Servidor desconectado, checar com Tech".
 *
 * Aqui existe um tipo de erro só, com o `code` e o `requestId` que a API
 * devolve, o que permite a tela decidir o que mostrar e o suporte achar a
 * linha exata no log a partir de um print.
 */
import type { RespostaErro } from '@tcm/shared';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
const TIMEOUT_MS = 20_000;

// Diagnóstico de origem. Quando o build sai sem `VITE_API_BASE_URL` (o caso do
// Cloudflare Pages, que não lê o `.env` local), o BASE cai no relativo e toda
// chamada vai para o próprio Pages, que não tem API — o sintoma é a tela em
// loading eterno com 404 em série no Network.
console.info(
  '[api] base=%o origem=%o env_definido=%o',
  BASE,
  typeof location !== 'undefined' ? location.origin : '(sem window)',
  import.meta.env.VITE_API_BASE_URL !== undefined,
);
if (import.meta.env.PROD && !import.meta.env.VITE_API_BASE_URL) {
  console.error(
    '[api] VITE_API_BASE_URL ausente neste build. As chamadas vão para %s/api/v1, ' +
      'que é o host do frontend e não a API. Defina a variável no build do Cloudflare Pages ' +
      'e refaça o deploy.',
    typeof location !== 'undefined' ? location.origin : '',
  );
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Array<{ campo?: string; mensagem: string }>;
  readonly requestId?: string;

  constructor(
    status: number,
    code: string,
    message: string,
    details: Array<{ campo?: string; mensagem: string }> = [],
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** Banco fora do ar ou API inalcançável — merece o modal, não um toast. */
  get ehIndisponibilidade(): boolean {
    return this.status === 503 || this.status === 0 || this.code === 'BANCO_INDISPONIVEL';
  }

  get ehNaoAutenticado(): boolean {
    return this.status === 401;
  }
}

interface OpcoesRequisicao {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  corpo?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  sinal?: AbortSignal;
}

function montarUrl(caminho: string, query?: OpcoesRequisicao['query']): string {
  const url = `${BASE}${caminho}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [chave, valor] of Object.entries(query)) {
    if (valor === undefined || valor === null || valor === '') continue;
    params.set(chave, String(valor));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/** `AbortError` de qualquer origem — DOMException no navegador, Error em teste. */
function ehAborto(erro: unknown): boolean {
  return erro instanceof Error && erro.name === 'AbortError';
}

export async function requisitar<T>(caminho: string, opcoes: OpcoesRequisicao = {}): Promise<T> {
  const externo = opcoes.sinal;

  // Sinal JÁ abortado quando a chamada começa: acontece quando o Query
  // cancela a consulta entre o render e o disparo. Sem esta linha o
  // `addEventListener` abaixo nunca dispara e a requisição sai assim mesmo —
  // uma ida à API que ninguém vai ler.
  if (externo?.aborted) throw new DOMException('Requisição cancelada.', 'AbortError');

  const controlador = new AbortController();
  // Distingue as DUAS causas de aborto. Sem esta flag, o cancelamento normal
  // do TanStack Query (troca de tela, digitação nova, componente desmontado)
  // era relatado no console como "timeout de 20000ms" e virava um ApiError de
  // status 0 — que `ehIndisponibilidade` classifica como servidor fora do ar.
  // Ou seja: navegar rápido acendia o modal de "servidor desconectado".
  let expirou = false;
  const timer = setTimeout(() => {
    expirou = true;
    controlador.abort();
  }, TIMEOUT_MS);

  // Respeita o cancelamento do TanStack Query e o timeout, os dois.
  const repassarAborto = (): void => controlador.abort();
  externo?.addEventListener('abort', repassarAborto, { once: true });

  const url = montarUrl(caminho, opcoes.query);
  const metodo = opcoes.metodo ?? 'GET';

  try {
    const resposta = await fetch(url, {
      method: metodo,
      headers: opcoes.corpo !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: opcoes.corpo !== undefined ? JSON.stringify(opcoes.corpo) : undefined,
      // Sem isto o cookie httpOnly de sessão não viaja.
      credentials: 'include',
      signal: controlador.signal,
    });

    if (resposta.status === 204) return undefined as T;

    const texto = await resposta.text();
    const tipo = resposta.headers.get('content-type') ?? '(sem content-type)';

    // Antes o `JSON.parse` estourava aqui e o catch lá embaixo transformava
    // tudo em SEM_CONEXAO, escondendo o status e o corpo reais. Quando a
    // resposta não é JSON, quem responde não é a API — é o host do frontend
    // devolvendo o index.html do SPA ou uma página de erro.
    let dados: unknown = null;
    if (texto) {
      try {
        dados = JSON.parse(texto);
      } catch {
        console.error(
          '[api] resposta não-JSON\n  %s %s\n  status: %d %s\n  content-type: %s\n  corpo (300 primeiros chars): %s',
          metodo,
          url,
          resposta.status,
          resposta.statusText,
          tipo,
          texto.slice(0, 300),
        );
        throw new ApiError(
          resposta.status,
          'RESPOSTA_NAO_JSON',
          `A resposta de ${url} não é JSON (HTTP ${resposta.status}, ${tipo}). ` +
            'Provavelmente a chamada está indo para o host do frontend, não para a API.',
        );
      }
    }

    if (!resposta.ok) {
      // 401 é resposta ESPERADA de quem ainda não entrou: a Portaria pergunta
      // `/auth/me` antes de montar qualquer tela. Gritar `console.error` nesse
      // caso enche o console de vermelho num fluxo que funciona — e esconde o
      // erro de verdade quando ele aparece. 4xx avisa, 5xx grita.
      const registrar =
        resposta.status === 401
          ? console.info
          : resposta.status < 500
            ? console.warn
            : console.error;
      registrar(
        '[api] %s %s → HTTP %d %s',
        metodo,
        url,
        resposta.status,
        resposta.statusText,
        dados,
      );
      const erro = (dados as RespostaErro | null)?.error;
      throw new ApiError(
        resposta.status,
        erro?.code ?? 'ERRO_DESCONHECIDO',
        erro?.message ?? `Falha na requisição (HTTP ${resposta.status}).`,
        erro?.details ?? [],
        erro?.requestId,
      );
    }

    return dados as T;
  } catch (erro) {
    if (erro instanceof ApiError) throw erro;
    if (ehAborto(erro)) {
      // Cancelamento de quem chamou: o Query desistiu desta consulta porque a
      // tela mudou. Não é falha, não vai para o console e o erro sobe CRU —
      // é assim que o TanStack Query reconhece um cancelamento e não marca a
      // consulta como quebrada.
      if (!expirou) throw erro;

      console.error('[api] %s %s → sem resposta em %dms (timeout)', metodo, url, TIMEOUT_MS);
      throw new ApiError(0, 'TIMEOUT', 'O servidor demorou demais para responder.');
    }
    // Aqui o fetch nem completou: DNS, CORS bloqueado ou API fora do ar. O
    // navegador só entrega "Failed to fetch" ao JS — o motivo exato fica na
    // aba Network / no próprio console, acima desta linha.
    console.error('[api] %s %s → falha de rede/CORS:', metodo, url, erro);
    throw new ApiError(
      0,
      'SEM_CONEXAO',
      'Não foi possível falar com o servidor. Verifique a rede e chame o suporte técnico.',
    );
  } finally {
    clearTimeout(timer);
    // Sem isto, um `signal` de vida longa acumula um ouvinte por requisição.
    externo?.removeEventListener('abort', repassarAborto);
  }
}

export const api = {
  get: <T,>(caminho: string, query?: OpcoesRequisicao['query'], sinal?: AbortSignal) =>
    requisitar<T>(caminho, { query, sinal }),
  post: <T,>(caminho: string, corpo?: unknown) => requisitar<T>(caminho, { metodo: 'POST', corpo }),
  put: <T,>(caminho: string, corpo?: unknown) => requisitar<T>(caminho, { metodo: 'PUT', corpo }),
  patch: <T,>(caminho: string, corpo?: unknown) =>
    requisitar<T>(caminho, { metodo: 'PATCH', corpo }),
  delete: <T,>(caminho: string) => requisitar<T>(caminho, { metodo: 'DELETE' }),
};
